/**
 * Page d'achat : un seul produit, un seul bouton.
 *
 * Trois arguments et rien d'autre. Une liste plus longue dilue : ce qui se vend
 * ici tient en une phrase, « tout est ouvert, pour toujours, et vos amis en
 * profitent ». Le prix est affiche en grand avec « paiement unique » colle a
 * lui, parce que la question qu'on se pose devant un prix est toujours « est-ce
 * que ca va se reprelever ».
 *
 * Le quatrieme argument, celui des invites, est le plus important pour ce jeu :
 * un joueur qui achete ouvre les modes payants a TOUTE sa table. Il ne s'achete
 * pas un privilege, il paie une tournee.
 */
import { useCallback, useState, type ReactElement } from 'react';
import { useT } from '../locales/useT.js';
import { navigate } from '../router.js';
import { Screen } from '../ui/Screen.js';
import { ChevronLeftIcon, CheckIcon, GiftIcon } from '../ui/icons.js';
import { useAccountStore } from '../account/accountStore.js';
import { AccountError, account } from '../net/account.js';

export function PurchaseScreen(): ReactElement {
  const t = useT();
  const profile = useAccountStore((state) => state.profile);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const buy = useCallback(() => {
    if (profile === null) return navigate('/account');
    setBusy(true);
    setError(null);
    void account
      .checkout()
      .then(({ url }) => {
        // Stripe heberge la page de paiement : on quitte le site, et l'on y
        // revient par `success_url`. Rien de sensible ne transite ici.
        window.location.href = url;
      })
      .catch((cause: unknown) => {
        setBusy(false);
        setError(
          cause instanceof AccountError && cause.message === 'unavailable'
            ? t('purchase.closed')
            : t('account.failed'),
        );
      });
  }, [profile, t]);

  const owned = profile?.paid === true;

  return (
    <Screen name="purchase" scroll>
      <main className="purchase">
        <div className="lobby__nav">
          <button type="button" className="button-back" onClick={() => navigate('/account')}>
            <ChevronLeftIcon />
            {t('common.back')}
          </button>
        </div>

        <header className="purchase__header">
          <span className="purchase__seal" aria-hidden="true">
            <GiftIcon size={26} />
          </span>
          <h1 className="purchase__title">{t('purchase.title')}</h1>
          <p className="purchase__subtitle">{t('purchase.subtitle')}</p>
        </header>

        <p className="purchase__price mono-tabular">
          {t('purchase.price')}
          <small>{t('purchase.once')}</small>
        </p>

        <ul className="purchase__list">
          {(['purchase.modes', 'purchase.noAds', 'purchase.avatars', 'purchase.guests'] as const).map(
            (key) => (
              <li key={key}>
                <CheckIcon size={16} />
                {t(key)}
              </li>
            ),
          )}
        </ul>

        {error !== null && <p className="account__error">{error}</p>}

        <footer className="purchase__footer">
          {owned ? (
            <p className="hint">{t('purchase.owned')}</p>
          ) : (
            <>
              <button
                type="button"
                className="button-primary button-block"
                disabled={busy}
                onClick={buy}
              >
                {t('purchase.buy')}
              </button>
              {profile === null && <p className="hint">{t('purchase.needAccount')}</p>}
            </>
          )}
        </footer>
      </main>
    </Screen>
  );
}
